import React, { useEffect, useState } from 'react';
import { Box, Tabs, Tab, Paper, Typography, Divider, Stack, IconButton, Tooltip } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import SelfReviewForm from './SelfReviewForm';
import ManagerReviewForm from './ManagerReviewForm';
import ManagerCommentsView from './ManagerCommentsView';
import { getAllCorporateUsers } from '../CorporateQueries';
import { getAllReviews } from './AnnualReviewQueries';
import AnnualReviewHeader from './AnnualReviewHeader';
import { getCorporateUser } from '../CorporateQueries';

const AnnualReviewIndex = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(0);
  const [canManagerReview, setCanManagerReview] = useState(false);
  const [selectedEmployeeEmail, setSelectedEmployeeEmail] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [reviewsCache, setReviewsCache] = useState([]);

  // Get form data from navigation state (employee path uses formData/formSchema, manager path uses formFromState/formSchemaFromState)
  const formFromState = location.state?.formFromState || location.state?.formData;
  const formSchemaFromState = location.state?.formSchemaFromState || location.state?.formSchema;
  const employeeData = location.state?.employeeData;
  const openManagerTab = location.state?.openManagerTab;
  const reviewData = location.state?.reviewData;
  const isManagerView = openManagerTab;

  const [details, setDetails] = useState({
    employeeName: '',
    title: '',
    manager: '',
    secondLevelManager: '',
    periodStart: formFromState?.start_date || '',
    periodEnd: formFromState?.end_date || '',
    date: new Date().toISOString().slice(0, 10),
    formId: formFromState?.id || '',
  });

  // Sync details when form state changes
  useEffect(() => {
    if (formFromState) {
      setDetails(prev => ({
        ...prev,
        employeeName: employeeData?.employee_name || employeeData?.employee_email || prev.employeeName,
        periodStart: formFromState.start_date || '',
        periodEnd: formFromState.end_date || '',
        formId: formFromState.id || '',
      }));
    }
  }, [formFromState, employeeData]);

  // Open manager tab when navigating from ManagerReviewsTable
  useEffect(() => {
    if (openManagerTab && canManagerReview) {
      setTab(0);
      if (employeeData?.employee_email) {
        setSelectedEmployeeEmail(employeeData.employee_email);
      }
    }
  }, [openManagerTab, canManagerReview, employeeData]);

  // Check if current user is a manager
  useEffect(() => {
    const checkManagerAccess = async () => {
      try {
        const currentUserEmail = localStorage.getItem('user') || '';
        if (!currentUserEmail) { setCanManagerReview(false); return; }
        const users = await getAllCorporateUsers();
        const isReportingL1 = users.some(u => String(u.reporting_l1 || '').trim().toLowerCase() === String(currentUserEmail).trim().toLowerCase());
        const isReportingL2 = users.some(u => String(u.reporting_l2 || '').trim().toLowerCase() === String(currentUserEmail).trim().toLowerCase());
        setCanManagerReview(isReportingL1 || isReportingL2);
      } catch (err) {
        console.error('[AnnualReview] Error checking manager access:', err);
        setCanManagerReview(false);
      }
    };
    checkManagerAccess();
  }, []);

  // Fetch all reviews once so children can filter locally
  const loadAllReviews = async () => {
    try {
      const res = await getAllReviews();
      const items = Array.isArray(res?.reviews) ? res.reviews
        : Array.isArray(res?.Items) ? res.Items
        : Array.isArray(res?.body?.reviews) ? res.body.reviews
        : Array.isArray(res?.body?.Items) ? res.body.Items
        : Array.isArray(res) ? res : [];
      if (items?.length) setReviewsCache(items);
    } catch (e) {
      console.error('[AnnualReview] Error loading reviews:', e);
    }
  };

  useEffect(() => {
    loadAllReviews();
  }, []);

  // Refresh reviews when switching to Manager Comments tab
  useEffect(() => {
    if (!isManagerView && tab === 1) {
      loadAllReviews();
    }
  }, [tab, isManagerView]);

  useEffect(() => {
    if (!canManagerReview && tab > 1) setTab(0);
  }, [canManagerReview, tab]);

  // Prefill header with selected employee details when in manager view
  useEffect(() => {
    const loadSelectedEmployeeDetails = async () => {
      try {
        if (!selectedEmployeeEmail) return;
        const shouldLoad = (canManagerReview && tab === 2) || isManagerView;
        if (!shouldLoad) return;
        const user = await getCorporateUser(selectedEmployeeEmail);
        if (user) {
          const raw = Array.isArray(user?.users) ? user.users[0] : (user?.user || user);
          const val = (obj, key) => {
            const v = obj?.[key];
            if (v && typeof v === 'object' && 'S' in v) return v.S || '';
            return v || '';
          };
          setDetails(prev => ({
            ...prev,
            employeeName: `${val(raw, 'first_name')} ${val(raw, 'last_name')}`.trim(),
            title: val(raw, 'job_title'),
            manager: val(raw, 'reporting_l1'),
            secondLevelManager: val(raw, 'reporting_l2'),
          }));
        }
      } catch (err) {
        console.error('[AnnualReview] Error loading selected employee details:', err);
      }
    };
    loadSelectedEmployeeDetails();
  }, [tab, selectedEmployeeEmail, canManagerReview, isManagerView]);

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={2} sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title={isManagerView ? "Back to Manager Reviews" : "Back to Reviews"}>
              <IconButton onClick={() => navigate(isManagerView ? '/ManagerReviews' : '/Reviews')} size="small">
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Annual Performance Review</Typography>
          </Stack>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {/* Common header above tabs */}
        <AnnualReviewHeader
          details={details}
          setDetails={setDetails}
          editable={false}
          autoPrefill={!isManagerView && tab === 0}
          showEmployeeNote={!isManagerView && tab === 0}
        />

        {!isManagerView && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="annual review tabs">
            <Tab label="Self Review" />
            <Tab label="Manager Review" />
          </Tabs>
        )}
        {isManagerView && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="manager review tabs">
            <Tab label="Manager Review" />
          </Tabs>
        )}
      </Paper>

      <Box sx={{ mt: 2 }}>
        {!isManagerView && tab === 0 && (
          <SelfReviewForm
            details={details}
            reviewsCache={reviewsCache}
            refreshCache={loadAllReviews}
            formSchema={formSchemaFromState}
          />
        )}
        {!isManagerView && tab === 1 && (
          <ManagerCommentsView
            reviewsCache={reviewsCache}
            formSchema={formSchemaFromState}
            periodStart={details.periodStart}
          />
        )}
        {isManagerView && tab === 0 && (
          <ManagerReviewForm
            disabled={!canManagerReview}
            selectedEmployeeEmail={selectedEmployeeEmail}
            selectedEmployeeId={selectedEmployeeId}
            reviewsCache={reviewsCache}
            onSelectEmployee={(email) => { setSelectedEmployeeEmail(email); setSelectedEmployeeId(''); }}
            details={details}
            refreshCache={loadAllReviews}
            formSchema={formSchemaFromState}
            hideEmployeeSelector={isManagerView}
            passedReviewData={reviewData}
          />
        )}
      </Box>
    </Box>
  );
};

export default AnnualReviewIndex;
